import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '@ui/authcard';
import { Button } from '@/ui/button';
import { Input } from '@/ui/input';
import { Label } from '@/ui/label';
import { cn } from '@/lib/utils';
import { useAuth } from '../../hooks/useAuth';
import { useAuthDialog } from '../../contexts/AuthDialogContext';
// import { formatPhoneNumberDisplay, toApiFormat } from '../../utils/phoneValidation';
import { useTranslation } from 'react-i18next';
import { logger } from '../../utils/logger';

interface OTPVerificationFormProps extends React.HTMLAttributes<HTMLDivElement> {
  onSuccess?: () => void;
}

const OTP_LENGTH = 6;
const createEmptyOtp = () => Array.from({ length: OTP_LENGTH }, () => '');
const sanitizeOtpValue = (value: string) => value.replace(/\D/g, '').slice(0, OTP_LENGTH);

export function OTPVerificationForm({ className, onSuccess, ...props }: OTPVerificationFormProps) {
  const [otp, setOtp] = useState<string[]>(createEmptyOtp);
  const [email, setEmail] = useState('');
  // const [phoneNumber, setPhoneNumber] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isResending, setIsResending] = useState(false);
  const [timeLeft, setTimeLeft] = useState(300); // 5 minutes = 300 seconds
  const [isExpired, setIsExpired] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { sendOTP, verifyOTP } = useAuth();
  const { showAuthDialog } = useAuthDialog();
  const { t } = useTranslation();
  const otpInputRefs = useRef<Array<HTMLInputElement | null>>([]);
  const otpCode = otp.join('');
  const purpose = searchParams.get('purpose') === 'login' ? 'login' : undefined;
  const loginChallengeId = searchParams.get('loginChallengeId') || undefined;

  useEffect(() => {
    // Get email and OTP from URL params
    const emailParam = searchParams.get('email');
    const otpParam = searchParams.get('otp');

    setEmail(emailParam || '');

    if (otpParam) {
      const sanitizedOtp = sanitizeOtpValue(otpParam);
      setOtp(Array.from({ length: OTP_LENGTH }, (_, index) => sanitizedOtp[index] ?? ''));
    } else {
      setOtp(createEmptyOtp());
    }
  }, [searchParams]);

  // Countdown timer effect
  useEffect(() => {
    if (timeLeft <= 0) {
      setIsExpired(true);
      return;
    }

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          setIsExpired(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft]);

  // Format time helper
  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const focusOtpInput = (index: number) => {
    otpInputRefs.current[index]?.focus();
  };

  const updateOtpDigits = (index: number, rawValue: string) => {
    const digits = sanitizeOtpValue(rawValue);

    if (!digits) {
      const nextOtp = [...otp];
      nextOtp[index] = '';
      setOtp(nextOtp);
      return;
    }

    const nextOtp = digits.length === OTP_LENGTH ? createEmptyOtp() : [...otp];
    const fillStart = digits.length === OTP_LENGTH ? 0 : index;
    const maxLength = Math.min(digits.length, OTP_LENGTH - fillStart);

    for (let offset = 0; offset < maxLength; offset += 1) {
      nextOtp[fillStart + offset] = digits[offset];
    }

    setOtp(nextOtp);

    const nextFocusIndex =
      maxLength === 1 ? Math.min(fillStart + 1, OTP_LENGTH - 1) : fillStart + maxLength - 1;
    requestAnimationFrame(() => focusOtpInput(nextFocusIndex));
  };

  const handleOTPChange = (index: number, value: string) => {
    updateOtpDigits(index, value);
  };

  const handleOTPPaste = (index: number, e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    updateOtpDigits(index, e.clipboardData.getData('text'));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (isExpired) {
      setError('OTP has expired. Please request a new code.');
      return;
    }

    if (otpCode.length !== OTP_LENGTH) {
      setError('Please enter the complete 6-digit code.');
      return;
    }

    setIsLoading(true);

    try {
      // const apiPhoneNumber = toApiFormat(phoneNumber);
      const response = await verifyOTP({
        email: email,
        otp: otpCode,
        purpose,
        loginChallengeId,
        trustDevice: purpose === 'login',
      });

      if (response.success) {
        const establishedSession =
          Boolean(response.accessToken && response.refreshToken && response.user) ||
          purpose === 'login';

        if (onSuccess) {
          onSuccess();
        } else if (establishedSession) {
          navigate('/');
        } else {
          showAuthDialog('login');
        }
      } else {
        setError(response.error || 'Invalid OTP. Please try again.');
      }
    } catch (error) {
      logger.error('OTP verification error:', error);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setIsResending(true);
    setError('');

    try {
      // const apiPhoneNumber = toApiFormat(phoneNumber);
      const response = await sendOTP({
        email: email,
        purpose,
        loginChallengeId,
      });

      if (response.success) {
        setError('');
        // Clear current OTP
        setOtp(createEmptyOtp());
        // Reset timer and expired state
        setTimeLeft(300);
        setIsExpired(false);
        // Focus first input
        requestAnimationFrame(() => focusOtpInput(0));
      } else {
        setError(response.error || 'Failed to resend OTP. Please try again.');
      }
    } catch (error) {
      logger.error('Resend OTP error:', error);
      setError('Failed to resend OTP. Please try again.');
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className={cn('flex flex-col gap-6', className)} {...props}>
      <Card className="overflow-hidden py-0">
        <CardContent className="p-6">
          <form onSubmit={handleSubmit}>
            <div className="flex flex-col gap-6">
              <div className="flex flex-col items-center text-center">
                <p className="text-balance text-muted-foreground">
                  We've sent a verification code to {email}
                </p>
                <div
                  className={`text-sm mt-2 ${isExpired ? 'text-red-600' : timeLeft <= 60 ? 'text-orange-600' : 'text-gray-600'}`}
                >
                  {isExpired ? 'Code expired' : `Code expires in ${formatTime(timeLeft)}`}
                </div>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                  {error}
                </div>
              )}

              <div className="grid gap-2">
                <Label>{t('auth.verification_code')}</Label>
                <div className="grid grid-cols-6 gap-2">
                  {[...Array(6)].map((_, i) => (
                    <Input
                      key={i}
                      type="text"
                      maxLength={1}
                      className="aspect-square text-center text-lg"
                      name={`otp-${i}`}
                      inputMode="numeric"
                      autoComplete={i === 0 ? 'one-time-code' : 'off'}
                      value={otp[i] || ''}
                      ref={(element) => {
                        otpInputRefs.current[i] = element;
                      }}
                      onChange={(e) => handleOTPChange(i, e.target.value)}
                      onPaste={(e) => handleOTPPaste(i, e)}
                      onKeyDown={(e) => {
                        if (e.key === 'Backspace' && !otp[i] && i > 0) {
                          focusOtpInput(i - 1);
                        }
                      }}
                      required
                    />
                  ))}
                </div>
              </div>

              <Button
                type="submit"
                className="w-full"
                disabled={isLoading || otpCode.length !== OTP_LENGTH || isExpired}
              >
                {isLoading ? 'Verifying...' : isExpired ? 'Code Expired' : 'Verify Email'}
              </Button>

              <div className="flex items-center justify-center text-sm">
                <span className="text-muted-foreground">
                  {isExpired ? 'Code expired.' : "Didn't receive the code?"}
                </span>
                <Button
                  type="button"
                  variant="link"
                  className="p-0 ml-1 h-auto"
                  onClick={handleResendOTP}
                  disabled={isResending}
                >
                  {isResending ? 'Sending...' : isExpired ? 'Get New Code' : 'Resend'}
                </Button>
              </div>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
